import { defineStore } from 'pinia';
import {
  buildAuthorizeUrl,
  createPkceChallenge,
  exchangeCodeForToken,
  saveVerifier,
  consumeVerifier,
} from '../services/oauth-pkce';

const TOKEN_KEY = 'corporate-cms-token';
const USER_KEY = 'corporate-cms-user';

interface GitLabUser {
  id: number;
  username: string;
  name: string;
  avatar_url?: string;
  email?: string;
}

interface AuthState {
  token: string | null;
  tokenExpiresAt: number | null;
  user: GitLabUser | null;
  hydrated: boolean;
}

function env() {
  return {
    gitlabBaseUrl: import.meta.env.VITE_GITLAB_BASE_URL || 'https://gitlab.com',
    clientId: import.meta.env.VITE_GITLAB_OAUTH_CLIENT_ID,
    redirectUri: import.meta.env.VITE_GITLAB_OAUTH_REDIRECT_URI,
  };
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: null,
    tokenExpiresAt: null,
    user: null,
    hydrated: false,
  }),
  getters: {
    isAuthenticated: (state) => !!state.token && (!state.tokenExpiresAt || state.tokenExpiresAt > Date.now()),
  },
  actions: {
    hydrate() {
      if (this.hydrated) return;
      try {
        const raw = sessionStorage.getItem(TOKEN_KEY);
        if (raw) {
          const parsed = JSON.parse(raw) as { token: string; expiresAt: number | null };
          this.token = parsed.token;
          this.tokenExpiresAt = parsed.expiresAt;
        }
        const rawUser = sessionStorage.getItem(USER_KEY);
        if (rawUser) this.user = JSON.parse(rawUser);
      } catch (_) {
        this.clear();
      }
      this.hydrated = true;
    },
    async startLogin(returnTo = '/') {
      const { gitlabBaseUrl, clientId, redirectUri } = env();
      if (!clientId || clientId === 'REPLACE_ME') {
        throw new Error('OAuth client ID non configurato. Imposta VITE_GITLAB_OAUTH_CLIENT_ID in .env.');
      }
      const { verifier, challenge } = await createPkceChallenge();
      const state = crypto.randomUUID();
      saveVerifier(verifier, state, returnTo);
      const url = buildAuthorizeUrl({
        gitlabBaseUrl,
        clientId,
        redirectUri,
        challenge,
        state,
        scope: 'api',
      });
      window.location.assign(url);
    },
    async handleCallback(code: string, state: string): Promise<string> {
      const { gitlabBaseUrl, clientId, redirectUri } = env();
      const record = consumeVerifier(state);
      if (!record) throw new Error('State PKCE non valido o verifier mancante.');
      const token = await exchangeCodeForToken({
        gitlabBaseUrl,
        clientId,
        redirectUri,
        code,
        verifier: record.verifier,
      });
      const expiresAt = token.expires_in ? Date.now() + token.expires_in * 1000 : null;
      this.token = token.access_token;
      this.tokenExpiresAt = expiresAt;
      sessionStorage.setItem(
        TOKEN_KEY,
        JSON.stringify({ token: token.access_token, expiresAt }),
      );
      await this.fetchUser();
      return record.returnTo;
    },
    async fetchUser() {
      if (!this.token) return;
      const { gitlabBaseUrl } = env();
      const res = await fetch(`${gitlabBaseUrl}/api/v4/user`, {
        headers: { Authorization: `Bearer ${this.token}` },
      });
      if (!res.ok) {
        this.clear();
        throw new Error(`Impossibile recuperare l'utente GitLab: ${res.status}`);
      }
      this.user = await res.json();
      sessionStorage.setItem(USER_KEY, JSON.stringify(this.user));
    },
    logout() {
      this.clear();
      window.location.assign(import.meta.env.VITE_CMS_BASE_PATH || '/');
    },
    clear() {
      this.token = null;
      this.tokenExpiresAt = null;
      this.user = null;
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);
    },
  },
});
